import { requestLaravel } from "../../../../../../utils/api";
import { pick } from "lodash";
import { stakeholderApi } from "../../ProjectInitiation-steps/core/_request";
import { ApiResponse } from "../../../../core/_models";
import { getProjectFieldSection, saveProjectFieldSection, withProjectFieldValues } from "../../../core/project-fields/_request";
import { ESG_FINAL_CHECK_TAB_SECTIONS, PROJECT_COMMUNICATION_TAB_SECTIONS, SUMMARY_OF_OPTIONS_TAB_SECTIONS, INSTITUTIONAL_READINESS_TAB_SECTIONS, StepTabSection, IFinalFeasibilityValidationData, IFinalFeasibilityValidation, IESGFinalCheck, ICostBenefitFinalModel, ICostBenefitFinalModelData, IFiscalSustainabilityIfsa, IFiscalSustainabilityIfsaData, IDecisionData, IInfrastructureMasterPlan, ILongTermAssetManagement, IEconomicAndCommercialViability, IImplementationStrategy, IRiskStatement, IEconomicMultiplierAndMacroeconomicsBenefits, ISupervisionReportingAndEvaluation, IDigitalTechnologyLeverage, IInstitutionalAndOperationalReadiness, IProjectCommunicationAndStakeholderEngagementPlan, IFinacialRequirmentsAndFundingstrategy, IProjectPhase, IProjectPhaseMilestone, IKeyVariablesSensitivity, ISummaryOfOptionConsidered, IOptionsConsidered } from "./_models";

const CONCEPT_AND_VIABILITY_STAGE_ID = 4;
export const STAGE_FOUR_KEY = "project_stage_4";
export const FINAL_FEASIBILITY_VALIDATION_SECTION = "final_feasibility_validation";

export const COST_BENEFIT_FINAL_MODEL_SECTION = "cost_benefit_final_model";
// The step key stays fiscal_sustainability_ifsa; its project-fields section is named ..._ifisa_module.
export const FISCAL_SUSTAINABILITY_IFISA_SECTION = "fiscal_sustainability_ifisa_module";
// No fields: its risk rows save on the risk-management API; the section holds its documents.
export const RISK_FINALISATION_ENGINE_SECTION = "risk_finalisation_engine";
// No fields: its phases save on their own API; the section holds its documents.
export const PROJECT_PLAN_SECTION = "project_plan";

// The fields each section saves; a field the section does not have is rejected.
const FINAL_FEASIBILITY_VALIDATION_FIELDS: (keyof IFinalFeasibilityValidation)[] = ["feasibility_status", "key_constraints_summary"];
const FISCAL_SUSTAINABILITY_IFISA_FIELDS: (keyof IFiscalSustainabilityIfsa)[] = ["final_fiscal_affordability_index", "final_contingent_liability_exposure", "long_term_fiscal_impact_score"];
const COST_BENEFIT_FINAL_MODEL_FIELDS: (keyof ICostBenefitFinalModel)[] = ["analysis_mode_selector", "total_project_cost", "total_economic_benefit", "cost_per_outcome_unit", "total_outcome_units_delivered"];

function readStageSection<T>(projectId: string | number, sectionKey: string, signal?: AbortSignal) {
    return getProjectFieldSection(projectId, CONCEPT_AND_VIABILITY_STAGE_ID, sectionKey, signal)
        .then((response) => withProjectFieldValues<T>(response));
}

function writeStageSection<T>(projectId: string | number, sectionKey: string, payload: object, signal?: AbortSignal) {
    return saveProjectFieldSection(projectId, CONCEPT_AND_VIABILITY_STAGE_ID, sectionKey, payload, signal)
        .then((response) => withProjectFieldValues<T>(response));
}

// Saves one tab's fields on its sub-section; the response holds only those fields. A tab
// without a sub-section (rows or documents that save on their own) sends nothing.
function writeStageTab<T extends object>(updateData: T & { project_id: number | string }, tab: StepTabSection<T> | undefined, savedMessage: string, signal?: AbortSignal) {
    if (!tab) {
        return Promise.resolve({ status: "ok", message: savedMessage, data: null });
    }
    return writeStageSection<Partial<T>>(updateData.project_id, tab.section, pick(updateData, tab.fields), signal);
}

// One read for the section values and the Readiness Score, which also needs the CBA/CEA and IFISA sections.
export function getFinalFeasibilityValidation(project_id: string | number, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IFinalFeasibilityValidationData>>(
        "/project-stage-four/final-feasibility-validation/get",
        { project_id: Number(project_id) },
        { signal }
    );
}

export function updateFinalFeasibilityValidation(project_id: string | number, values: IFinalFeasibilityValidation, signal?: AbortSignal) {
    return writeStageSection<IFinalFeasibilityValidation>(project_id, FINAL_FEASIBILITY_VALIDATION_SECTION, pick(values, FINAL_FEASIBILITY_VALIDATION_FIELDS), signal);
}

export function getCostBenefitFinalModel(project_id: string | number, signal?: AbortSignal) {
    return readStageSection<ICostBenefitFinalModelData>(project_id, COST_BENEFIT_FINAL_MODEL_SECTION, signal);
}

export function updateCostBenefitFinalModel(project_id: string | number, values: ICostBenefitFinalModel, signal?: AbortSignal) {
    return writeStageSection<ICostBenefitFinalModel>(project_id, COST_BENEFIT_FINAL_MODEL_SECTION, pick(values, COST_BENEFIT_FINAL_MODEL_FIELDS), signal);
}

export function fetchFiscalSustainabilityIfsaData(project_id: string | number, signal?: AbortSignal) {
    return readStageSection<IFiscalSustainabilityIfsaData>(project_id, FISCAL_SUSTAINABILITY_IFISA_SECTION, signal);
}

export function updateFiscalSustainabilityIfsaData(project_id: string | number, values: IFiscalSustainabilityIfsa, signal?: AbortSignal) {
    return writeStageSection<IFiscalSustainabilityIfsa>(project_id, FISCAL_SUSTAINABILITY_IFISA_SECTION, pick(values, FISCAL_SUSTAINABILITY_IFISA_FIELDS), signal);
}

/* ==========================================================================
   Stage 4 steps on the common section API
   Each step reads its seeder section and saves only that section's fields.
========================================================================== */

// The steps that pass the whole form as the read payload use only its project_id.
type StageSectionReadPayload = { project_id: number | string } & Record<string, unknown>;

export const LONG_TERM_ASSET_MANAGEMENT_SECTION = "long_term_asset_management";

export function getLongTermAssetManagement(project_id: string | number, signal?: AbortSignal) {
    return readStageSection<ILongTermAssetManagement>(project_id, LONG_TERM_ASSET_MANAGEMENT_SECTION, signal);
}

const LONG_TERM_ASSET_MANAGEMENT_FIELDS: (keyof ILongTermAssetManagement)[] = ["expenditure_statement", "funding_statement", "cashflow_statement", "contingent_liability_statement"];

export function updateLongTermAssetManagement(updateData: ILongTermAssetManagement & { project_id: number | string }, signal?: AbortSignal) {
    return writeStageSection<ILongTermAssetManagement>(updateData.project_id, LONG_TERM_ASSET_MANAGEMENT_SECTION, pick(updateData, LONG_TERM_ASSET_MANAGEMENT_FIELDS), signal);
}

export const ESG_FINAL_CHECK_SECTION = "social_environmental_gender_impact_final_check";

export function getESGFinalCheck(project_id: string | number, signal?: AbortSignal) {
    return readStageSection<IESGFinalCheck>(project_id, ESG_FINAL_CHECK_SECTION, signal);
}

export function updateESGFinalCheck(updateData: IESGFinalCheck & { project_id: number | string }, tabId: number, signal?: AbortSignal) {
    return writeStageTab(updateData, ESG_FINAL_CHECK_TAB_SECTIONS[tabId], "Social, Environmental & Gender Impact Final Check saved", signal);
}

export const INFRASTRUCTURE_MASTER_PLAN_SECTION = "infrastructure_master_plan";
const INFRASTRUCTURE_MASTER_PLAN_FIELDS: (keyof IInfrastructureMasterPlan)[] = ["master_plan_definition"];

export function getInfrastructureMasterPlan(project_id: string | number, signal?: AbortSignal) {
    return readStageSection<IInfrastructureMasterPlan>(project_id, INFRASTRUCTURE_MASTER_PLAN_SECTION, signal);
}

export function updateInfrastructureMasterPlan(updateData: IInfrastructureMasterPlan & { project_id: number | string }, signal?: AbortSignal) {
    return writeStageSection<IInfrastructureMasterPlan>(updateData.project_id, INFRASTRUCTURE_MASTER_PLAN_SECTION, pick(updateData, INFRASTRUCTURE_MASTER_PLAN_FIELDS), signal);
}

export const SUPERVISION_REPORTING_EVALUATION_SECTION = "supervision_reporting_evaluation";
const SUPERVISION_REPORTING_EVALUATION_FIELDS: (keyof ISupervisionReportingAndEvaluation)[] = ["supervision_framework", "me_framework_kpi_data_source", "me_responsibility_feedback_loops", "reporting_frequency_format", "evaluation_schedule", "reporting_framework", "evaluation_framework", "incentives_for_successful_delivery", "penalties_for_non_performance"];

export function getSupervisionReportingAndEvaluation(payload: StageSectionReadPayload, signal?: AbortSignal) {
    return readStageSection<ISupervisionReportingAndEvaluation>(payload.project_id, SUPERVISION_REPORTING_EVALUATION_SECTION, signal);
}

export function updateSupervisionReportingAndEvaluation(updateData: ISupervisionReportingAndEvaluation & { project_id: number | string }, signal?: AbortSignal) {
    return writeStageSection<ISupervisionReportingAndEvaluation>(updateData.project_id, SUPERVISION_REPORTING_EVALUATION_SECTION, pick(updateData, SUPERVISION_REPORTING_EVALUATION_FIELDS), signal);
}

export const PROJECT_COMMUNICATION_SECTION = "project_communication_stakeholder_engagement_plan";
export function getProjectCommunicationAndStakeholderEngagementPlan(payload: StageSectionReadPayload, signal?: AbortSignal) {
    return readStageSection<IProjectCommunicationAndStakeholderEngagementPlan>(payload.project_id, PROJECT_COMMUNICATION_SECTION, signal);
}

export function updateProjectCommunicationAndStakeholderEngagementPlan(updateData: IProjectCommunicationAndStakeholderEngagementPlan & { project_id: number | string }, tabId: number, signal?: AbortSignal) {
    return writeStageTab(updateData, PROJECT_COMMUNICATION_TAB_SECTIONS[tabId], "Project Communication & Stakeholder Engagement Plan saved", signal);
}

export const DIGITAL_TECHNOLOGY_LEVERAGE_SECTION = "digital_technology_leverage";
const DIGITAL_TECHNOLOGY_LEVERAGE_FIELDS: (keyof IDigitalTechnologyLeverage)[] = ["digital_technology_leverage"];

export function getDigitalTechnologyLeverage(payload: StageSectionReadPayload, signal?: AbortSignal) {
    return readStageSection<IDigitalTechnologyLeverage>(payload.project_id, DIGITAL_TECHNOLOGY_LEVERAGE_SECTION, signal);
}

export function updateDigitalTechnologyLeverage(updateData: IDigitalTechnologyLeverage & { project_id: number | string }, signal?: AbortSignal) {
    return writeStageSection<IDigitalTechnologyLeverage>(updateData.project_id, DIGITAL_TECHNOLOGY_LEVERAGE_SECTION, pick(updateData, DIGITAL_TECHNOLOGY_LEVERAGE_FIELDS), signal);
}

export const INSTITUTIONAL_OPERATIONAL_READINESS_SECTION = "institutional_operational_readiness";
export function getInstitutionalAndOperationalReadiness(payload: StageSectionReadPayload, signal?: AbortSignal) {
    return readStageSection<IInstitutionalAndOperationalReadiness>(payload.project_id, INSTITUTIONAL_OPERATIONAL_READINESS_SECTION, signal);
}

export function updateInstitutionalAndOperationalReadiness(updateData: IInstitutionalAndOperationalReadiness & { project_id: number | string }, tabId: number, signal?: AbortSignal) {
    return writeStageTab(updateData, INSTITUTIONAL_READINESS_TAB_SECTIONS[tabId], "Institutional & Operational Readiness saved", signal);
}

export const FINANCIAL_REQUIREMENTS_SECTION = "financial_requirements_funding_strategy";
const FINANCIAL_REQUIREMENTS_FIELDS: (keyof IFinacialRequirmentsAndFundingstrategy)[] = ["cost_breakdown", "funding_model", "revenue_strategy"];

export function getFinancialRequirementsAndFundingStrategy(payload: StageSectionReadPayload, signal?: AbortSignal) {
    return readStageSection<IFinacialRequirmentsAndFundingstrategy>(payload.project_id, FINANCIAL_REQUIREMENTS_SECTION, signal);
}

export function updateFinancialRequirementsAndFundingStrategy(updateData: IFinacialRequirmentsAndFundingstrategy & { project_id: number | string }, signal?: AbortSignal) {
    return writeStageSection<IFinacialRequirmentsAndFundingstrategy>(updateData.project_id, FINANCIAL_REQUIREMENTS_SECTION, pick(updateData, FINANCIAL_REQUIREMENTS_FIELDS), signal);
}

export const ECONOMIC_MULTIPLIER_SECTION = "economic_multiplier_macroeconomic_benefits";
const ECONOMIC_MULTIPLIER_FIELDS: (keyof IEconomicMultiplierAndMacroeconomicsBenefits)[] = ["gdp_contribution_estimate", "regional_economic_growth_effects", "direct_employment", "indirect_employment", "induced_employment", "sector_economic_multiplier", "supply_chain_local_economic_development", "productivity_trade_facilitation", "poverty_reduction_equity_impact", "fiscal_returns_to_government", "sdg_macroeconomic_policy_alignment", "macroeconomic_risks_sensitivity"];

export function getEconomicMultiplierAndMacroeconomicsBenefits(payload: StageSectionReadPayload, signal?: AbortSignal) {
    return readStageSection<IEconomicMultiplierAndMacroeconomicsBenefits>(payload.project_id, ECONOMIC_MULTIPLIER_SECTION, signal);
}

export function updateEconomicMultiplierAndMacroeconomicsBenefits(updateData: IEconomicMultiplierAndMacroeconomicsBenefits & { project_id: number | string }, signal?: AbortSignal) {
    return writeStageSection<IEconomicMultiplierAndMacroeconomicsBenefits>(updateData.project_id, ECONOMIC_MULTIPLIER_SECTION, pick(updateData, ECONOMIC_MULTIPLIER_FIELDS), signal);
}

export const IMPLEMENTATION_STRATEGY_SECTION = "implementation_strategy";
const IMPLEMENTATION_STRATEGY_FIELDS: (keyof IImplementationStrategy)[] = ["implementation_strategy_overview", "asisa_investment_framework_alignment", "esg_policy_statement_integration_approach", "esg_environmental", "esg_social", "esg_governance", "institutional_investor_funding_mobilisation_strategy", "blended_finance_structure", "institutional_investor_participation_requirements", "due_diligence_framework_standards", "impact_measurement_framework", "impact_reporting_disclosure_commitments", "stewardship_arrangements_crisa_alignment", "asisa_disclosure_framework", "asisa_risk_management_framework", "implementation_plan_phasing_schedule", "implementation_monitoring_evaluation", "exit_strategy_liquidity_provision"];

export function getImplementationStrategy(payload: StageSectionReadPayload, signal?: AbortSignal) {
    return readStageSection<IImplementationStrategy>(payload.project_id, IMPLEMENTATION_STRATEGY_SECTION, signal);
}

export function updateImplementationStrategy(updateData: IImplementationStrategy & { project_id: number | string }, signal?: AbortSignal) {
    return writeStageSection<IImplementationStrategy>(updateData.project_id, IMPLEMENTATION_STRATEGY_SECTION, pick(updateData, IMPLEMENTATION_STRATEGY_FIELDS), signal);
}

export const SUMMARY_OF_OPTIONS_SECTION = "summary_of_options_considered";
export function getSummaryOfOptionConsidered(payload: StageSectionReadPayload, signal?: AbortSignal) {
    return readStageSection<ISummaryOfOptionConsidered>(payload.project_id, SUMMARY_OF_OPTIONS_SECTION, signal);
}

export function updateSummaryOfOptionConsidered(updateData: ISummaryOfOptionConsidered & { project_id: number | string }, tabId: number, signal?: AbortSignal) {
    return writeStageTab(updateData, SUMMARY_OF_OPTIONS_TAB_SECTIONS[tabId], "Summary of Options Considered saved", signal);
}

export const ECONOMIC_COMMERCIAL_VIABILITY_SECTION = "economic_commercial_viability";
const ECONOMIC_COMMERCIAL_VIABILITY_FIELDS: (keyof IEconomicAndCommercialViability)[] = ["value_for_money_financial_assessment", "summary_financial_vfm_findings", "benefit_analysis_financial_quantification", "summary_financial_benefit_findings", "risk_evaluation_financial_risk_costing", "summary_financial_risk_findings", "economic_multipliers_financial_returns", "summary_economic_multiplier_financial_findings", "economies_of_scale_whole_life_cost_savings", "summary_economies_of_scale_financial_findings", "cost_effectiveness_unit_cost_metrics", "summary_cea_financial_findings", "social_economic_financial_monetisation", "summary_social_economic_financial_findings"];

export function getEconomicAndCommercialViability(payload: StageSectionReadPayload, signal?: AbortSignal) {
    return readStageSection<IEconomicAndCommercialViability>(payload.project_id, ECONOMIC_COMMERCIAL_VIABILITY_SECTION, signal);
}

export function updateEconomicAndCommercialViability(updateData: IEconomicAndCommercialViability & { project_id: number | string }, signal?: AbortSignal) {
    return writeStageSection<IEconomicAndCommercialViability>(updateData.project_id, ECONOMIC_COMMERCIAL_VIABILITY_SECTION, pick(updateData, ECONOMIC_COMMERCIAL_VIABILITY_FIELDS), signal);
}

export const RISK_STATEMENT_SECTION = "risk_statement_sensitivity_analysis";
const RISK_STATEMENT_FIELDS: (keyof IRiskStatement)[] = ["risk_statement_problem_statement", "risk_statement_risk_context", "sensitivity_analysis_overview", "sensitivity_analysis_conclusion", "overall_risk_statement"];

export function getRiskStatement(payload: StageSectionReadPayload, signal?: AbortSignal) {
    return readStageSection<IRiskStatement>(payload.project_id, RISK_STATEMENT_SECTION, signal);
}

export function updateRiskStatement(updateData: IRiskStatement & { project_id: number | string }, signal?: AbortSignal) {
    return writeStageSection<IRiskStatement>(updateData.project_id, RISK_STATEMENT_SECTION, pick(updateData, RISK_STATEMENT_FIELDS), signal);
}

export function saveProjectStageFour(payload: { project_id: string; dk_stage: string }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<void>>(
        "/project-stage-four/stage/submit",
        { project_id: payload.project_id },
        { signal }
    );
}

export function getFinalInvestmentDecisionEngine(payload: { project_id: number }, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IDecisionData | null>>(
        "/project-stage-four/final-investment-decision/get",
        payload,
        { signal }
    );
}

export function getProjectStage4Phases(project_id: string, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IProjectPhase[]>>(
        "/project-stage-four/project-plan-phases/list",
        { project_id },
        { signal }
    );
}

export function storeOrUpdateProjectStage4Phase(
    updateData: IProjectPhase & { project_id: number },
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<IProjectPhase>>(
        "/project-stage-four/project-plan-phases/save",
        updateData,
        { signal }
    );
}

export function deleteProjectStage4Phase(phase_id: number, project_id: number, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IProjectPhase>>(
        "/project-stage-four/project-plan-phases/delete",
        { phase_id, project_id },
        { signal }
    );
}

export function getProjectStage4Milestones(project_id: number, phase_id: number, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IProjectPhaseMilestone[]>>(
        "/project-stage-four/project-plan-milestones/list",
        { project_id, phase_id },
        { signal }
    );
}

export function storeOrUpdateProjectStage4Milestone(
    updateData: IProjectPhaseMilestone & { project_id: number; phase_id: number },
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<IProjectPhaseMilestone>>(
        "/project-stage-four/project-plan-milestones/save",
        updateData,
        { signal }
    );
}

export function deleteProjectStage4Milestone(milestone_id: number, project_id: number, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<{ milestone_id: number }>>(
        "/project-stage-four/project-plan-milestones/delete",
        { milestone_id, project_id },
        { signal }
    );
}

// Stage 4's stakeholders, on the endpoints Stage 0 uses (tbl_project_stakeholders, stage_id project_stage_4).
export const stageFourStakeholderApi = stakeholderApi(STAGE_FOUR_KEY);

export function getKeyVariablesSensitivityResults(project_id: number | string, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IKeyVariablesSensitivity[]>>(
        "/project-stage-four/key-variables-sensitivity-results/list",
        { project_id },
        { signal }
    );
}

export function storeOrUpdateKeyVariablesSensitivityResult(
    updateData: IKeyVariablesSensitivity & { project_id: number },
    signal?: AbortSignal
) {
    return requestLaravel<ApiResponse<IKeyVariablesSensitivity>>(
        "/project-stage-four/key-variables-sensitivity-results/save",
        updateData,
        { signal }
    );
}

export function deleteKeyVariablesSensitivityResult(row_id: number, project_id: number, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<{ row_id: number }>>(
        "/project-stage-four/key-variables-sensitivity-results/delete",
        { row_id, project_id },
        { signal }
    );
}

// Options Considered are the Stage 4 options of the shared project options (tbl_project_options,
// stage_id project_stage_4); Stage 4 edits each option's description and assessment only.
export function getOptionsConsidered(project_id: number | string, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<IOptionsConsidered[]>>(
        "/project/options/list",
        { project_id: Number(project_id), stage_id: STAGE_FOUR_KEY },
        { signal }
    );
}

const OPTION_ASSESSMENT_FIELDS: (keyof IOptionsConsidered)[] = ["row_id", "description", "advantages", "disadvantages", "preliminary_cost_estimate", "preliminary_benefits_estimate", "value_for_money_assessment"];

export function storeOrUpdateSummaryOfOptionsConsidered(payload: IOptionsConsidered & { project_id: number }) {
    return requestLaravel<ApiResponse<IOptionsConsidered>>(
        "/project/options/assessment/save",
        { ...pick(payload, OPTION_ASSESSMENT_FIELDS), project_id: payload.project_id, stage_id: STAGE_FOUR_KEY }
    );
}
